// Building contents: putting an accepted item into a building (insertInto), finding and taking out items a
// player can drag from a building (processor slots and out, Packer buffer and out, Gallery queue), turning
// Crate counts back into items, and the one overflow rule (IMPLEMENTATION §8 "eject, then sell"): items that
// must leave a building go to free adjacent tiles (N/E/S/W), the rest are sold at 100% as manual income with
// a +$ floater (a `sell` event with token 0, never undoable).

import { isMatcher, recipeOf, slotFams } from './bdefs.ts';
import { DIRS, stepCell } from './core/grid.ts';
import { applyPermille } from './core/num.ts';
import type { Cell, Dir, Int } from './core/types.ts';
import type { Ctx } from './ctx.ts';
import {
  discover,
  famDef,
  findBuilding,
  findItem,
  itemValue,
  newId,
  restoreItem,
} from './entities.ts';
import type { FamId } from './gen/ids.ts';
import { book } from './ledger.ts';
import type { Building, CrateB, FloorItem, ItemRef, ProcessorB, SaleWhere } from './state.ts';
import { freeUnreserved, reserve, sinkOf } from './topology.ts';

/** A new item record (fresh id) of an existing or newly made kind; always discovered (M2 decision 12). */
export function makeRef(c: Ctx, fam: FamId, tier: Int): ItemRef {
  discover(c, fam, tier);
  return { id: newId(c.s), fam, tier };
}

/** Clears the drag if `id` was the held entity (it was consumed or left the board). */
export function dropHeld(c: Ctx, id: Int): void {
  if (c.s.transient.held === id) c.s.transient.held = null;
}

/**
 * Puts an item the building accepted (rules/accept) into it. `hand` = 1 when dropped by the player (Gallery
 * payouts for it count as manual income, R9). Coal entering a Smelter tank becomes heat and is booked as
 * burned (R6); an item entering a Crate becomes a count (its id ends).
 */
export function insertInto(c: Ctx, b: Building, r: ItemRef, hand: 0 | 1): void {
  c.ev.push({ e: 'insert', id: r.id, bid: b.id, fam: r.fam, tier: r.tier, hand });
  switch (b.type) {
    case 'crate':
      b.fam = b.fam ?? r.fam;
      b.counts[r.tier - 1] = (b.counts[r.tier - 1] ?? 0) + 1;
      dropHeld(c, r.id);
      return;
    case 'chute':
      b.carry = { id: r.id, fam: r.fam, tier: r.tier };
      b.prog = 0;
      reserve(c, sinkOf(c, b), b.carry);
      c.ev.push({ e: 'chuteStart', bid: b.id, id: r.id });
      return;
    case 'packer':
      b.lockFam = b.lockFam ?? r.fam;
      b.buf.push({ id: r.id, fam: r.fam, tier: r.tier });
      return;
    case 'gallery':
      b.queue.push({ id: r.id, fam: r.fam, tier: r.tier, hand });
      b.win.supplied[b.win.idx] = (b.win.supplied[b.win.idx] ?? 0) + 1;
      return;
    case 'deposit':
    case 'drill':
    case 'dock':
      throw new Error(`insertInto: ${b.type} takes no items`);
    default: {
      if (b.type === 'smelter' && r.fam === c.d.buildings.smelter.fuel) {
        b.heat += famDef(c.d, r.fam).fuelHeat[r.tier - 1] ?? 0;
        book(c.s, c.d, r.fam, r.tier, 'burned', 1);
        dropHeld(c, r.id);
        return;
      }
      const fams = slotFams(c.d, b);
      for (let i = 0; i < fams.length; i++) {
        if (fams[i] === r.fam) {
          (b.slots[i] as ItemRef[]).push({ id: r.id, fam: r.fam, tier: r.tier });
          return;
        }
      }
      throw new Error(`insertInto: ${b.type} has no slot for ${r.fam}`);
    }
  }
}

/** Where a draggable stored item sits: `slot` ≥ 0 an input slot / Packer buffer / Gallery queue, −1 `out`. */
export interface StoredAt {
  b: Building;
  slot: Int;
  idx: Int;
  item: ItemRef;
}

function listOf(b: Building, slot: Int): ItemRef[] | null {
  switch (b.type) {
    case 'caster':
    case 'grinder':
    case 'smelter':
    case 'press':
    case 'loader':
    case 'gunsmith':
      return slot < 0 ? b.out : (b.slots[slot] ?? null);
    case 'packer':
      return slot < 0 ? b.out : slot === 0 ? b.buf : null;
    case 'gallery':
      return slot === 0 ? b.queue : null;
    default:
      return null;
  }
}

/** The stored item at (bid, slot, idx) if its id matches. */
export function storedAt(c: Ctx, bid: Int, slot: Int, idx: Int, id: Int): StoredAt | null {
  const b = findBuilding(c.s, bid);
  if (b === null || b.ghost !== null) return null;
  const list = listOf(b, slot);
  if (list === null || !Number.isInteger(idx) || idx < 0 || idx >= list.length) return null;
  const item = list[idx] as ItemRef;
  return item.id === id ? { b, slot, idx, item } : null;
}

/** Any draggable stored item with this id (grab / held checks). */
export function findStored(c: Ctx, id: Int): StoredAt | null {
  for (const b of c.s.buildings) {
    if (b.ghost !== null) continue;
    const last = 'slots' in b ? b.slots.length - 1 : 0;
    for (let slot = -1; slot <= last; slot++) {
      const list = listOf(b, slot);
      if (list === null) continue;
      for (let i = 0; i < list.length; i++) {
        if ((list[i] as ItemRef).id === id) return { b, slot, idx: i, item: list[i] as ItemRef };
      }
    }
  }
  return null;
}

/** A floor item, a building or a draggable stored item with this id exists (grab / held). */
export function grabbable(c: Ctx, id: Int): boolean {
  return findItem(c.s, id) !== null || findBuilding(c.s, id) !== null || findStored(c, id) !== null;
}

/** Releases the drag when its entity vanished (consumed by a machine, fired, sold, merged away). */
export function releaseVanishedHeld(c: Ctx): void {
  const held = c.s.transient.held;
  if (held !== null && !grabbable(c, held)) c.s.transient.held = null;
}

/** Takes a stored item out of its building (the caller places it). */
export function takeStored(_c: Ctx, at: StoredAt): ItemRef {
  const list = listOf(at.b, at.slot) as ItemRef[];
  list.splice(at.idx, 1);
  if (at.b.type === 'packer' && at.b.buf.length === 0 && at.b.out.length === 0) at.b.lockFam = null;
  return { id: at.item.id, fam: at.item.fam, tier: at.item.tier };
}

/** One Crate item of `tier` as a new item record (count − 1; the family clears when the Crate empties). */
export function takeFromCrate(c: Ctx, b: CrateB, tier: Int): ItemRef {
  const fam = b.fam as FamId;
  b.counts[tier - 1] = (b.counts[tier - 1] ?? 0) - 1;
  const r = makeRef(c, fam, tier);
  let left = 0;
  for (const k of b.counts) left += k;
  if (left === 0) b.fam = null;
  return r;
}

/** Books an item-credit that is not an undoable sale (overflow, cancelled-ghost credit): manual income. */
export function creditItems(
  c: Ctx,
  items: readonly ItemRef[],
  where: SaleWhere,
  x: Int,
  y: Int,
): Int {
  let value = 0;
  for (const r of items) {
    value += applyPermille(itemValue(c.d, r.fam, r.tier), c.d.economy.sellPermille.counter);
    book(c.s, c.d, r.fam, r.tier, 'sold', 1);
    dropHeld(c, r.id);
  }
  if (items.length === 0) return 0;
  c.s.wallet.cash += value;
  c.s.wallet.lifetime += value;
  c.s.income.accT += value;
  c.ev.push({ e: 'sell', token: 0, value, where, ids: items.map((r) => r.id), x, y });
  return value;
}

function inCells(cells: readonly Cell[], x: Int, y: Int): boolean {
  for (const p of cells) if (p.x === x && p.y === y) return true;
  return false;
}

/**
 * Overflow (R11): each item goes to the next free tile adjacent to (x, y) in N/E/S/W order (keeping its id;
 * never a tile in `avoid`, nor a floor tile a chute chain is delivering onto); whatever finds no tile is
 * sold at 100% (manual income, `sell` event, token 0).
 */
export function ejectOrSell(
  c: Ctx,
  items: readonly ItemRef[],
  x: Int,
  y: Int,
  avoid: readonly Cell[] = [],
): void {
  const sold: ItemRef[] = [];
  let k = 0;
  for (const r of items) {
    let placed = false;
    while (k < 4) {
      const n = stepCell(x, y, DIRS[k] as Dir);
      k++;
      if (freeUnreserved(c, n.x, n.y) && !inCells(avoid, n.x, n.y)) {
        const it: FloorItem = { id: r.id, fam: r.fam, tier: r.tier, x: n.x, y: n.y };
        restoreItem(c, it);
        c.ev.push({ e: 'spawn', id: r.id, fam: r.fam, tier: r.tier, x: n.x, y: n.y, from: 0 });
        placed = true;
        break;
      }
    }
    if (!placed) sold.push(r);
  }
  creditItems(c, sold, 'overflow', x, y);
}

/**
 * Undoes a running cycle: the set it consumed comes back as input items (one per recipe input at the cycle's
 * tier; Smelter heat returns to the tank). Units are conserved and no work is gained. Used when a machine is
 * merged or removed.
 */
export function revertProc(c: Ctx, b: ProcessorB): ItemRef[] {
  const p = b.proc;
  if (p === null) return [];
  const recipe = recipeOf(c.d, b);
  const fams = isMatcher(b.type) ? slotFams(c.d, b) : recipe.inputs.slice(0, 1);
  const back: ItemRef[] = [];
  for (const f of fams) back.push(makeRef(c, f, p.tier));
  if (recipe.heatNeed.length > 0) b.heat += recipe.heatNeed[p.tier - 1] ?? 0;
  b.proc = null;
  b.prog = 0;
  return back;
}

/** Every item a building holds, as records (Crate counts become new items), for overflow / sale. */
export function drainContents(c: Ctx, b: Building): ItemRef[] {
  const out: ItemRef[] = [];
  const take = (xs: ItemRef[]): void => {
    for (const r of xs) out.push({ id: r.id, fam: r.fam, tier: r.tier });
    xs.length = 0;
  };
  switch (b.type) {
    case 'crate':
      if (b.fam !== null) {
        for (let t = b.counts.length; t >= 1; t--) {
          while ((b.counts[t - 1] ?? 0) > 0) out.push(takeFromCrate(c, b, t));
        }
      }
      b.fam = null;
      break;
    case 'chute':
      if (b.carry !== null) out.push(b.carry);
      b.carry = null;
      b.prog = 0;
      break;
    case 'packer':
      take(b.buf);
      take(b.out);
      b.lockFam = null;
      b.prog = 0;
      break;
    case 'gallery':
      if (b.gun !== null) out.push(b.gun);
      b.gun = null;
      take(b.queue);
      break;
    case 'dock':
      take(b.queue);
      break;
    case 'deposit':
    case 'drill':
      break;
    default:
      out.push(...revertProc(c, b));
      for (const s of b.slots) take(s);
      take(b.out);
      break;
  }
  return out;
}
